import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';

import { SousService } from 'app/models/sous-service';
import { SousServiceService } from 'app/services/sous-Service/sous-service.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-sous-service',
  templateUrl: './modifier-sous-service.component.html',
  styleUrls: ['./modifier-sous-service.component.scss']
})
export class ModifierSousServiceComponent implements OnInit {
  // Sous-service data
  sousService: SousService = new SousService();
  availableCategories: string[] = [];
  idSousService: string | null = null;
  parentServiceId: string | null = null; // Store parent service ID for navigation

  // Images management
  existingImages: string[] = [];
  newImages: string[] = [];
  deletedImages: string[] = [];
  
  // Main image (cover) upload
  mainImageFile: File | null = null;
  isUploadingMainImage: boolean = false;
  
  // Gallery upload state
  selectedFiles: FileList | null = null;
  isUploadingGallery: boolean = false;
  
  // Form state
  isSubmitting: boolean = false;

  // ViewChild references for file inputs
  @ViewChild('mainImageInput') mainImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryFileInput') galleryFileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private sousServiceService: SousServiceService,
    private awsService: AwscontrolService,
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.idSousService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadSousServiceDetails();
    
    this.sousServiceService.refreshNedeed.subscribe(() => {
      this.loadSousServiceDetails();
    });

    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => console.log('✅ AWS S3 Connection Healthy:', response),
      error: (err) => console.warn('⚠️ AWS S3 Connection Warning:', err)
    });
  }

  loadSousServiceDetails(): void {
    if (this.idSousService) {
      this.sousServiceService.getSousService(this.idSousService).subscribe({
        next: (data: any) => {
          this.sousService = data;
          
          // Extract parent service ID
          if (data.service?.id) {
            this.parentServiceId = data.service.id.toString();
          } else if (data.serviceId) {
            this.parentServiceId = data.serviceId.toString();
          }
          
          if (!this.sousService.image_url) {
            this.sousService.image_url = '';
          }
          this.existingImages = data.listimages || [];
          this.newImages = [];
          this.deletedImages = [];
          this.initializeCategories(data);
        },
        error: (error) => {
          console.error('Error loading sous-service:', error);
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: 'Impossible de charger les détails du sous-service',
            confirmButtonColor: '#f44336'
          });
        }
      });
    }
  }

  private initializeCategories(data: any): void {
    if (data['service'] && data['service'].categoryList) {
      this.availableCategories = data['service'].categoryList;
    } else if (this.sousService.category) {
      this.availableCategories = [this.sousService.category];
    } else {
      this.availableCategories = [];
    }
  }

  // ----- TEXT FORMATTING -----
  formatText(command: string): void {
    if (!this.sousService.description) this.sousService.description = '';
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousService.description.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
      case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
    }
    const newText = this.sousService.description.substring(0, start) + formattedText + this.sousService.description.substring(end);
    this.sousService.description = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // ----- TRIGGER FILE INPUTS -----
  triggerMainImageUpload(): void {
    if (!this.isUploadingMainImage) {
      this.mainImageInput.nativeElement.click();
    }
  }

  triggerGalleryUpload(): void {
    if (!this.isUploadingGallery) {
      this.galleryFileInput.nativeElement.click();
    }
  }

  // ----- MAIN IMAGE (COVER) UPLOAD -----
  onMainImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) return;
    this.mainImageFile = file;
    this.uploadMainImage();
  }

  uploadMainImage(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMainImage = true;
    this.awsService.uploadFiles([this.mainImageFile], 'sous-service-main-images').subscribe({
      next: (response) => {
        this.isUploadingMainImage = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.sousService.image_url = response.urls[0];
          Swal.fire({
            icon: 'success',
            title: 'Image principale téléchargée',
            text: 'L\'image de couverture a été mise à jour',
            confirmButtonColor: '#4caf50',
            timer: 1500,
            showConfirmButton: false
          });
          if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
          this.mainImageFile = null;
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploadingMainImage = false;
        console.error('Main image upload error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Erreur de téléchargement',
          text: 'Impossible de télécharger l\'image principale',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  removeMainImage(): void {
    const oldImageUrl = this.sousService.image_url;
    if (!oldImageUrl) {
      this.sousService.image_url = '';
      return;
    }
    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(oldImageUrl).subscribe({
          next: () => {
            this.sousService.image_url = '';
            Swal.fire({
              icon: 'success',
              title: 'Supprimée',
              text: 'L\'image principale a été supprimée',
              timer: 1500,
              showConfirmButton: false
            });
          },
          error: (err) => {
            console.error('Delete error:', err);
            Swal.fire({
              icon: 'error',
              title: 'Erreur',
              text: 'Impossible de supprimer l\'image du serveur',
              confirmButtonColor: '#f44336'
            });
          }
        });
      }
    });
  }

  // ----- GALLERY IMAGES UPLOAD -----
  onGalleryFilesSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedFiles = files;
      this.uploadGalleryImages();
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'Veuillez sélectionner une image (JPEG, PNG, JPG, GIF, WEBP)', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'La taille maximale autorisée est de 5MB', confirmButtonColor: '#ff9800' });
      return false;
    }
    return true;
  }

  uploadGalleryImages(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    this.isUploadingGallery = true;
    const filesArray = Array.from(this.selectedFiles);
    const validFiles = filesArray.filter(file => this.validateImageFile(file));
    if (validFiles.length === 0) {
      this.isUploadingGallery = false;
      return;
    }
    this.awsService.uploadFiles(validFiles, 'sous-service-images').subscribe({
      next: (response) => {
        this.isUploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.newImages.push(...response.urls);
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.count} image(s) ont été téléchargée(s)`,
            confirmButtonColor: '#4caf50',
            timer: 1500,
            showConfirmButton: false
          });
          if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploadingGallery = false;
        console.error('Upload error:', err);
        Swal.fire({ icon: 'error', title: 'Erreur de téléchargement', text: 'Impossible de télécharger les images', confirmButtonColor: '#f44336' });
      }
    });
  }

  deleteExistingImage(index: number): void {
    const imageUrl = this.existingImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: () => {
            this.existingImages.splice(index, 1);
            this.deletedImages.push(imageUrl);
            Swal.fire({ icon: 'success', title: 'Supprimée', text: 'L\'image a été supprimée', timer: 1500, showConfirmButton: false });
          },
          error: (err) => {
            console.error('Delete error:', err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de supprimer l\'image du serveur', confirmButtonColor: '#f44336' });
          }
        });
      }
    });
  }

  deleteNewImage(index: number): void {
    const imageUrl = this.newImages[index];
    Swal.fire({
      title: 'Supprimer cette nouvelle image ?',
      text: 'L\'image sera définitivement supprimée du serveur.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: () => {
            this.newImages.splice(index, 1);
            Swal.fire({ icon: 'success', title: 'Supprimée', text: 'L\'image a été supprimée', timer: 1500, showConfirmButton: false });
          },
          error: (err) => {
            console.error('Delete error:', err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de supprimer l\'image', confirmButtonColor: '#f44336' });
          }
        });
      }
    });
  }

  // ----- FORM SUBMISSION (website removed) -----
  updateSousService(): void {
    if (!this.sousService.name || this.sousService.name.trim() === '') {
      Swal.fire({ icon: 'warning', title: 'Nom requis', text: 'Veuillez saisir un nom', confirmButtonColor: '#ff9800' });
      return;
    }
    if (!this.sousService.category || this.sousService.category.trim() === '') {
      Swal.fire({ icon: 'warning', title: 'Catégorie requise', text: 'Veuillez sélectionner une catégorie', confirmButtonColor: '#ff9800' });
      return;
    }

    this.isSubmitting = true;

    const finalImageList = [...this.existingImages, ...this.newImages];
    const sousServiceData = {
      name: this.sousService.name.trim(),
      category: this.sousService.category.trim(),
      description: this.sousService.description?.trim() || '',
      prix: this.sousService.prix?.trim() || '',
      image_url: this.sousService.image_url?.trim() || '',
      listimages: finalImageList
      // website removed
    };

    if (!this.idSousService) {
      Swal.fire({ icon: 'error', title: 'ID manquant', text: 'Impossible de mettre à jour sans ID', confirmButtonColor: '#f44336' });
      this.isSubmitting = false;
      return;
    }

    this.sousServiceService.updateSousService(this.idSousService, sousServiceData).subscribe({
      next: () => {
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: 'Sous-service mis à jour avec succès',
          confirmButtonColor: '#4caf50',
          timer: 2000,
          showConfirmButton: false
        }).then(() => this.goToParentService());
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la mise à jour';
    if (error.status === 400) errorMessage = 'Données invalides';
    else if (error.status === 401) errorMessage = 'Session expirée, veuillez vous reconnecter';
    else if (error.status === 403) errorMessage = 'Permissions insuffisantes';
    else if (error.status === 404) errorMessage = 'Sous-service introuvable';
    else if (error.status === 500) errorMessage = 'Erreur interne du serveur';
    Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
  }

  // ----- NAVIGATION (redirect to parent service) -----
  private goToParentService(): void {
    if (this.parentServiceId) {
      this.router.navigate(['/list-Sous-Services', this.parentServiceId]);
    } else {
      this.location.back();
    }
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications ?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loadSousServiceDetails();
        this.newImages = [];
        this.deletedImages = [];
        if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
      }
    });
  }

  navigateBack(): void {
    this.goToParentService();
  }

  cancel(): void {
    this.goToParentService();
  }

  getFileNameFromUrl(url: string): string {
    return url.substring(url.lastIndexOf('/') + 1);
  }
}